import { isSessionEndedError } from '@papan/api-client'
import { useState, useEffect, useCallback, useRef } from 'react'
import { Clock, RotateCcw, X, Loader2, AlertCircle, FileText } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { useConfirm, useToast, useMyCapabilities, useRetentionPurgeConfirm, RetentionPurgeReceipt, type RetentionPurgeOutcomeView } from '@papan/core-ui/ui'
import { appVersionsApi, type AppVersion } from '@papan/files/client'
import { restoreErrorText } from './restoreErrorText'

/** Blank → inherit (not a set), else a whole number in [1, 500]. Same
 *  contract as VersionPanel's per-file retention input. */
function parseRetentionInput(text: string): number | null {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isInteger(n) || n < 1 || n > 500) return null
  return n
}

interface AppVersionPanelProps {
  /** The `.pdapp` folder's file id — the app-versions API is keyed on it. */
  pdappId: string
  /** The `.pdapp` folder's display name (e.g. `Child miniapp.pdapp`), shown as
   *  the panel subtitle exactly like `VersionPanel` shows its file name — the
   *  app's history is presented as "History" of the app, not a separate concept. */
  pdappName: string
  /** The tenant the pdapp lives in — gates the Restore action on
   *  `manage_org:write`, same gate `VersionPanel` uses for its per-file
   *  retention controls. */
  tenantId: string
  onClose: () => void
  /** Navigates to Settings → Version Retention's "App versions" tab, deep-
   *  linked to this pdapp. Mirrors `VersionPanel`'s `onManageRetention` —
   *  the "Manage" button only renders when a caller wires it. */
  onManageRetention?: () => void
  /** Preview the app's main HTML as it was at the selected version — called
   *  with the version's `previewFile` (the pinned `index.html`). The parent
   *  renders the read-only per-file `VersionPreview` for it. `versionId` empty
   *  clears the preview. `appVersionNumber` is the number the panel labelled
   *  (so the preview badge reads `v3`, not index.html's own file-version
   *  number). Only wired where a preview surface exists. */
  onPreview?: (fileId: string, versionId: string, isLatest: boolean, appVersionNumber: number) => void
}

function formatRelativeTime(dateStr: string): string {
  if (!dateStr) return ''
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  if (Number.isNaN(then)) return ''
  const diffMs = now - then
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  return new Date(dateStr).toLocaleDateString()
}

/** Maps the server's `source` (Task 3 `AUTHORING_SOURCES`) to its i18n key.
 *  Falls back to `sourceEditor` for an unrecognized value rather than
 *  rendering nothing. */
function sourceKey(source: string): 'sourceEditor' | 'sourceAgent' | 'sourceUpload' | 'sourceCopy' | 'sourceRestore' {
  switch (source) {
    case 'agent': return 'sourceAgent'
    case 'upload': return 'sourceUpload'
    case 'copy': return 'sourceCopy'
    case 'restore': return 'sourceRestore'
    case 'editor':
    default:
      return 'sourceEditor'
  }
}

export function AppVersionPanel({ pdappId, pdappName, tenantId, onClose, onManageRetention, onPreview }: AppVersionPanelProps) {
  const [appVersions, setAppVersions] = useState<AppVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Select-then-restore, exactly like the per-file VersionPanel: clicking an
  // entry selects it; the restore action lives in one bar at the bottom.
  const [selectedAppVersionId, setSelectedAppVersionId] = useState<string | null>(null)
  const [restoring, setRestoring] = useState(false)

  const confirm = useConfirm()
  const toast = useToast()
  const { t } = useTranslation('files')

  // Gate exactly as VersionPanel does (#590 gating pass): restoring an app
  // version rewrites every member's copy of the app's source, so it needs
  // `manage_org:write`, not mere read access to the history.
  const isPersonal = tenantId.startsWith('p_')
  const { can } = useMyCapabilities(tenantId, isPersonal, undefined)
  const canRestore = can('manage_org', 'write')
  /** #1534 I2: an app reached through a route into another org — its history is shown, but restoring it and its
   *  "Keep versions" belong to the owning org (the server answers `restorable: false` / `editable: false`). */
  const [restorable, setRestorable] = useState(true)

  // ── "Keep versions" — per-PDApp app-version retention (#612 follow-up) ──
  // Same read-for-everyone / edit-gated-on-manage_org:write split VersionPanel
  // uses for its per-file retention section.
  const [retention, setRetention] = useState<{ override: number | null; resolved: number; source: 'pdapp' | 'tenant' | 'shipped'; editable?: boolean } | null>(null)
  const [retentionLoading, setRetentionLoading] = useState(true)
  const [retentionError, setRetentionError] = useState<string | null>(null)
  const [retentionInput, setRetentionInput] = useState('')
  const [retentionSaving, setRetentionSaving] = useState(false)
  const [retentionActionError, setRetentionActionError] = useState<string | null>(null)
  const purgeConfirm = useRetentionPurgeConfirm({ testId: 'app-retention-purge' })
  const [purgeOutcome, setPurgeOutcome] = useState<RetentionPurgeOutcomeView | null>(null)
  /** `fetchAppVersions` is declared below the retention handlers; they refresh the list through this. */
  const fetchAppVersionsRef = useRef<(() => Promise<void>) | null>(null)
  const resetPurge = purgeConfirm.reset
  useEffect(() => { resetPurge(); setPurgeOutcome(null) }, [pdappId, resetPurge])

  const fetchRetention = useCallback(async () => {
    setRetentionLoading(true)
    setRetentionError(null)
    let reasked = false
    try {
      const result = await appVersionsApi.getRetention(pdappId)
      setRetention(result)
      setRetentionInput(result.override != null ? String(result.override) : '')
    } catch (err) {
      if (isSessionEndedError(err)) { reasked = true; void fetchRetention(); return }   // #1316: asked again in the new session
      setRetentionError(t('appVersionPanel.keep.keepLoadFailed'))
      console.error('[AppVersionPanel] Failed to load app-version retention:', err)
    } finally {
      if (!reasked) setRetentionLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdappId])

  useEffect(() => {
    fetchRetention()
  }, [fetchRetention])

  const parsedRetentionInput = parseRetentionInput(retentionInput)

  const handleSetRetention = useCallback(async () => {
    if (parsedRetentionInput == null) return
    setRetentionSaving(true)
    setRetentionActionError(null)
    try {
      // #1443 — previewed (the app versions past it and the file versions only they pinned), confirmed, then saved
      const res = await purgeConfirm.run(
        () => appVersionsApi.retentionPurgePreview(pdappId, parsedRetentionInput),
        (purge) => appVersionsApi.setRetention(pdappId, parsedRetentionInput, { purge }),
      )
      if (res === null) return
      setPurgeOutcome(res.purged || res.queued ? res : null)
      await fetchRetention()
      // the list must not offer deleted snapshots (a queued purge has not run yet: its receipt says so)
      if (res.purged?.appVersions) await fetchAppVersionsRef.current?.()
    } catch (err) {
      setRetentionActionError(t('appVersionPanel.keep.keepSetFailed'))
      console.error('[AppVersionPanel] Failed to set app-version retention:', err)
    } finally {
      setRetentionSaving(false)
    }
  }, [pdappId, parsedRetentionInput, fetchRetention, t, purgeConfirm.run])

  const handleClearRetention = useCallback(async () => {
    setRetentionSaving(true)
    setRetentionActionError(null)
    try {
      const res = await purgeConfirm.run(
        () => appVersionsApi.retentionPurgePreview(pdappId, null),
        (purge) => appVersionsApi.clearRetention(pdappId, { purge }),
      )
      if (res === null) return
      setPurgeOutcome(res.purged || res.queued ? res : null)
      await fetchRetention()
      if (res.purged?.appVersions) await fetchAppVersionsRef.current?.()
    } catch (err) {
      setRetentionActionError(t('appVersionPanel.keep.keepClearFailed'))
      console.error('[AppVersionPanel] Failed to clear app-version retention:', err)
    } finally {
      setRetentionSaving(false)
    }
  }, [pdappId, fetchRetention, t, purgeConfirm.run])

  const fetchAppVersions = useCallback(async () => {
    setLoading(true)
    setError(null)
    let reasked = false
    try {
      const result = await appVersionsApi.list(pdappId)
      setAppVersions(result.appVersions)
      setRestorable(result.restorable !== false)
    } catch (err) {
      if (isSessionEndedError(err)) { reasked = true; void fetchAppVersions(); return }   // #1316: asked again in the new session
      setError(t('appVersionPanel.loadFailed'))
      console.error('[AppVersionPanel] Failed to load app versions:', err)
    } finally {
      if (!reasked) setLoading(false)
    }
  }, [pdappId, t])
  fetchAppVersionsRef.current = fetchAppVersions

  useEffect(() => {
    fetchAppVersions()
  }, [fetchAppVersions])

  const handleRestore = useCallback(async (appVersionId: string) => {
    const confirmed = await confirm({
      title: t('appVersionPanel.confirmTitle'),
      message: t('appVersionPanel.confirmBody'),
      type: 'warning',
      confirmText: t('appVersionPanel.restore'),
    })
    if (!confirmed) return

    setRestoring(true)
    try {
      const result = await appVersionsApi.restore(pdappId, appVersionId)
      toast.success(t('appVersionPanel.restored', { restored: result.restored }))
      setSelectedAppVersionId(null)
      onPreview?.('', '', false, 0)
      await fetchAppVersions()
    } catch (err) {
      // #1397 — a damaged stored copy refuses the restore whole: the server's own words say so
      toast.error(restoreErrorText(err, t('appVersionPanel.restoreFailed')))
      console.error('[AppVersionPanel] Restore failed:', err)
    } finally {
      setRestoring(false)
    }
  }, [pdappId, confirm, toast, t, fetchAppVersions, onPreview])

  // Select a version: highlight it AND preview its main HTML (`previewFile`)
  // through the parent's read-only VersionPreview. A snapshot with no root
  // HTML clears any open preview rather than leaving a stale one.
  const handleSelect = useCallback((version: AppVersion, isLatest: boolean) => {
    setSelectedAppVersionId(version.id)
    if (version.previewFile) onPreview?.(version.previewFile.fileId, version.previewFile.versionId, isLatest, version.versionNumber)
    else onPreview?.('', '', false, 0)
  }, [onPreview])

  return (
    <div className="h-full flex flex-col themed-bg-secondary" data-testid="app-version-panel">
      {/* Header — presented as the app's "History" (same label as the per-file
          VersionPanel), with the PDApp's name as the subtitle below. */}
      <div className="h-10 border-b themed-border flex items-center justify-between px-3 shrink-0">
        <div className="flex items-center gap-1.5">
          <Clock size={14} className="themed-text-muted" />
          <span className="text-xs themed-text-muted uppercase tracking-wide">History</span>
        </div>
        <button
          onClick={onClose}
          className="p-0.5 rounded hover:themed-bg-tertiary themed-text-muted hover:themed-text transition-colors"
          title="Close version history"
          aria-label="Close version history"
          data-testid="app-version-panel-close"
        >
          <X size={12} />
        </button>
      </div>

      {/* PDApp name — mirrors VersionPanel's file-name row */}
      <div className="px-3 py-2 border-b themed-border shrink-0">
        <div className="flex items-center gap-1.5">
          <FileText size={12} className="themed-text-muted shrink-0" />
          <span className="text-xs themed-text truncate">{pdappName}</span>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin themed-text-muted" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <AlertCircle size={24} className="mx-auto mb-2 text-red-400 opacity-60" />
            <p className="text-xs text-red-400">{error}</p>
            <button
              onClick={fetchAppVersions}
              className="mt-2 text-xs themed-text-muted hover:themed-text underline"
            >
              {t('appVersionPanel.retry')}
            </button>
          </div>
        </div>
      ) : appVersions.length === 0 ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center themed-text-muted">
            <Clock size={24} className="mx-auto mb-2 opacity-30" />
            <p className="text-xs">{t('appVersionPanel.empty')}</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {/* Version list — clickable entries, identical shape to the per-file
              VersionPanel: `v{N}` with a `(current)` marker on the newest (the
              list is newest-first), the source and author beneath. No
              per-version file/changed count — an app-version is a whole-app
              snapshot, and the count read as inconsistent beside the file
              panel. Selecting an entry surfaces the restore bar below. */}
          <div className="overflow-y-auto flex-1">
            {appVersions.map((version, index) => {
              const isLatest = index === 0
              return (
              <button
                key={version.id}
                onClick={() => handleSelect(version, isLatest)}
                className={`w-full text-left px-3 py-2 border-b themed-border transition-colors ${
                  selectedAppVersionId === version.id
                    ? 'themed-bg-tertiary'
                    : 'hover:themed-bg-tertiary/50'
                }`}
                data-testid="app-version-entry"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs themed-text font-medium">
                    v{version.versionNumber}
                    {isLatest && <span className="ml-1 text-[10px] themed-text-muted">(current)</span>}
                  </span>
                  <span className="text-[10px] themed-text-muted">
                    {formatRelativeTime(version.createdAt)}
                  </span>
                </div>
                <div className="mt-0.5">
                  <span className="text-[10px] themed-text-muted truncate">
                    {t(`appVersionPanel.${sourceKey(version.source)}`)}
                  </span>
                </div>
                {version.createdBy && (
                  <div className="text-[10px] themed-text-muted mt-0.5 truncate opacity-60">
                    {version.createdBy.email || version.createdBy.name}
                  </div>
                )}
              </button>
              )
            })}
          </div>

          {/* Restore the selected version — one bar at the bottom, mirroring
              VersionPanel. Only a NON-current selection is restorable
              (restoring the current version is a no-op, and the file panel's
              "Discard Changes" branch has no app-version analogue). Gated on
              manage_org:write, the same grant the per-entry control used. */}
          {canRestore && restorable && selectedAppVersionId && selectedAppVersionId !== appVersions[0]?.id && (
            <div className="px-3 py-2 border-t themed-border shrink-0">
              <button
                onClick={() => handleRestore(selectedAppVersionId)}
                disabled={restoring}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded
                  bg-amber-600 hover:bg-amber-500 text-white transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="app-version-restore"
              >
                {restoring ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RotateCcw size={12} />
                )}
                <span>{restoring ? t('appVersionPanel.restoring') : t('appVersionPanel.restoreThisVersion')}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Keep versions — per-PDApp app-version retention (#612 follow-up).
          The SECTION and its resolved "Keeping N app versions." line are
          shown to everyone who can open the history (read-only info); only
          the editable field + Set/Clear buttons are gated on
          `manage_org:write`, same split VersionPanel uses for its per-file
          retention section. */}
      <div className="px-3 py-2 border-t themed-border shrink-0">
        <span className="text-[10px] themed-text-muted uppercase tracking-wide">
          {t('appVersionPanel.keep.keepTitle')}
        </span>
        {retentionLoading ? (
          <div className="mt-1.5 flex items-center">
            <Loader2 size={12} className="animate-spin themed-text-muted" />
          </div>
        ) : retentionError ? (
          <p className="mt-1 text-[10px] text-red-400">{retentionError}</p>
        ) : retention && (
          <>
            <p className="mt-1 text-[10px] themed-text-muted leading-relaxed">
              {retention.source === 'pdapp'
                ? t('appVersionPanel.keep.keepResolved', { count: retention.resolved })
                : t('appVersionPanel.keep.keepResolvedInherited', { count: retention.resolved })}
            </p>
            {canRestore && retention.editable !== false && (
              <>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={retentionInput}
                    onChange={(e) => setRetentionInput(e.target.value)}
                    placeholder={t('appVersionPanel.keep.keepPlaceholder')}
                    disabled={retentionSaving}
                    aria-label={t('appVersionPanel.keep.keepInputLabel')}
                    className="themed-input border rounded px-1.5 py-0.5 text-[10px] w-16 outline-none focus:border-blue-500 disabled:opacity-50"
                  />
                  <button
                    onClick={handleSetRetention}
                    disabled={retentionSaving || parsedRetentionInput == null}
                    className="px-2 py-0.5 text-[10px] rounded themed-btn-secondary disabled:opacity-50"
                  >
                    {t('appVersionPanel.keep.keepSet')}
                  </button>
                  {retention.override != null && (
                    <button
                      onClick={handleClearRetention}
                      disabled={retentionSaving}
                      className="px-2 py-0.5 text-[10px] rounded themed-btn-secondary disabled:opacity-50"
                    >
                      {t('appVersionPanel.keep.keepClear')}
                    </button>
                  )}
                  {onManageRetention && (
                    <button
                      onClick={onManageRetention}
                      className="px-2 py-0.5 text-[10px] rounded themed-btn-secondary"
                    >
                      {t('appVersionPanel.keep.keepManage')}
                    </button>
                  )}
                </div>
                {retentionInput.trim() !== '' && parsedRetentionInput == null && (
                  <p className="mt-1 text-[10px] text-red-400">{t('appVersionPanel.keep.keepInvalid')}</p>
                )}
                {purgeConfirm.dialog}
                <div className="mt-1"><RetentionPurgeReceipt outcome={purgeOutcome} testId="app-retention-purged" /></div>
                {retentionActionError && (
                  <p className="mt-1 text-[10px] text-red-400">{retentionActionError}</p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
